import { type FC } from 'react';
import { useTranslations } from 'next-intl';

import { useStore } from '@nanostores/react';

import { SIZES } from '@app/constants';

import { StatusPins } from '@widgets/merchant';

import { $multiDecisionStore, multiDecisionActions } from '@features/merchant';

import routes from '@shared/routes';
import { SelectableCardLayout } from '@shared/ui-kit';
import { Box, Card, Flex } from '@shared/ui-kit';

import { type Merchant } from '@api/services/merchants';

import { CardBody, CardFooter, CardHeader } from './_components';

interface Props {
  merchant: Merchant;
}

const MerchantCard: FC<Props> = ({ merchant }) => {
  const t = useTranslations('Merchant');

  const { isActive, merchantIds } = useStore($multiDecisionStore);

  return (
    <SelectableCardLayout
      isActive={isActive}
      selectId={merchant.id}
      selectedIds={merchantIds}
      selectItem={() => multiDecisionActions.addMerchant(merchant.id)}
      removeItem={() => multiDecisionActions.removeMerchant(merchant.id)}
      hint={t('select')}
    >
      <Card
        data-testid="merchant-card"
        position="relative"
        px="6"
        py="4"
        minH={SIZES.merchantCardHeight}
        _hover={{ bg: 'customAlpha.100' }}
      >
        <Flex
          direction="column"
          justifyContent="space-between"
          rowGap="6"
        >
          <Flex
            direction="column"
            rowGap="2.5"
          >
            <CardHeader
              id={merchant.id}
              name={merchant.name}
              decision={merchant.decision}
              decision_applied_at={merchant.decision_applied_at}
              decision_applied_full_name={merchant.decision_applied_full_name}
            />
            <CardBody
              id={merchant.id}
              tin={merchant.tin}
              last_imported_at={merchant.last_imported_at}
              marketplacesCount={merchant.marketplace_counters.total}
            />
          </Flex>

          <CardFooter
            id={merchant.id}
            tags={merchant.tags}
            assignedTo={merchant.assigned_to}
            assignedFullName={merchant.assigned_full_name}
          />
        </Flex>

        <StatusPins marketplace_counters={merchant.marketplace_counters} />

        {/* LINK TO MERCHANT PAGE */}
        <Box
          as="a"
          href={routes.merchant(merchant.id)}
          className="bg-link"
          aria-label="merchant-link"
          data-testid="merchant_link"
          target="_blank"
          rel="noopener noreferrer"
        />
      </Card>
    </SelectableCardLayout>
  );
};

export default MerchantCard;
